import {
  BehaviorSubject,
  Observable,
  from,
  of,
  throwError,
  timeout,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { StoreResolver } from 'src/app/appStorage/store.resolver';
import { map, catchError } from 'rxjs/operators';
import { MenuServices } from './menu.service';
import { KioskResolver } from '../../kiosk-code/service/kiosk.resolver';
import { NavController } from '@ionic/angular';
import { MenuStorageService } from 'src/app/appServices/menu-storage.service';

@Injectable({
  providedIn: 'root',
})
export class MenuResolver {
  selectedMenuIndex = new BehaviorSubject(0);
  searchItem = new BehaviorSubject<string | null>(null);

  clientType!: string;
  menu: any[] = [];
  private hasResolvedMenu = false;

  // Share the same in-flight load between every caller. This avoids duplicate
  // API/IndexedDB work when the resolver and the page ask for the menu together.
  private loadPromise: Promise<any[]> | null = null;
  private loadIsForced = false;
  private readonly API_REQUEST_TIMEOUT_MS = 8000;

  constructor(
    private _menuService: MenuServices,
    private _storeResolver: StoreResolver,
    private _kioskResolver: KioskResolver,
    private navCtrl: NavController,
    private menuStorage: MenuStorageService
  ) {
    this.subscribeKioskDetails();
  }

  /**
   * subscribe kiosk details
   */
  subscribeKioskDetails() {
    this._kioskResolver.kioskDetails.subscribe((res) => {
      if (res) {
        this.clientType = res.clientType;
      }
    });
  }

  /**
   * get menu - loads from IndexedDB first, then API
   */
  getMenu(forceGet: boolean = false): Observable<any> {
    // Return cached menu if available and not forcing refresh
    if (!forceGet && this.menu.length > 0) {
      return of(this.menu);
    }

    if (!this.clientType) {
      this.navCtrl.navigateBack('/home');
      return of(null);
    }

    if (this.loadPromise) {
      if (forceGet && !this.loadIsForced) {
        // A forced refresh must not be downgraded to the IndexedDB result of an
        // already running normal load. Queue exactly one API refresh behind it.
        const currentLoad = this.loadPromise;
        const forcedLoad = currentLoad
          .catch(() => undefined)
          .then(() => this.loadMenuInternal(true));

        return from(this.trackLoad(forcedLoad, true));
      }

      if (!forceGet && this.loadIsForced) {
        // A normal caller may join a queued refresh while the preceding cache
        // load is still finishing. If the API refresh fails, keep the usable
        // cache instead of turning that caller into a blank/error page.
        return from(
          this.loadPromise.catch((error) => {
            if (this.menu.length > 0) {
              return this.menu;
            }
            throw error;
          })
        );
      }

      return from(this.loadPromise);
    }

    return from(this.trackLoad(this.loadMenuInternal(forceGet), forceGet));
  }

  /**
   * Read the best locally available snapshot without ever contacting the API.
   *
   * Keeping this operation separate from getMenuFromApi() lets the page render
   * immediately while still treating only a real HTTP response as a server
   * validation. An empty array is a valid cached menu and must not be confused
   * with "not loaded yet".
   */
  getCachedMenu(): Observable<any[] | null> {
    if (this.hasResolvedMenu) {
      return of(this.menu);
    }

    if (!this.clientType) {
      return of(null);
    }

    return from(
      this._storeResolver
        .getSelectedStore()
        .then(async (store) => {
          if (!store?.menuId) {
            return null;
          }

          const cachedMenu = await this.menuStorage.loadMenu(
            store.menuId,
            this.clientType
          );

          if (!Array.isArray(cachedMenu)) {
            return null;
          }

          this.menu = cachedMenu;
          this.hasResolvedMenu = true;
          return cachedMenu;
        })
        .catch((error) => {
          console.warn('Failed to read cached menu:', error);
          return null;
        })
    );
  }

  /** Always resolves from the server (or joins the current forced request). */
  getMenuFromApi(): Observable<any[]> {
    return this.getMenu(true);
  }

  private trackLoad(
    request: Promise<any[]>,
    forceGet: boolean
  ): Promise<any[]> {
    let sharedRequest!: Promise<any[]>;
    sharedRequest = request
      .then((menu) => {
        this.menu = menu;
        this.hasResolvedMenu = true;
        return menu;
      })
      .catch((error) => {
        console.error('Failed to load menu:', error);
        throw error;
      })
      .finally(() => {
        // Only clear the request that actually completed.
        if (this.loadPromise === sharedRequest) {
          this.loadPromise = null;
          this.loadIsForced = false;
        }
      });

    this.loadPromise = sharedRequest;
    this.loadIsForced = forceGet;
    return sharedRequest;
  }

  /**
   * Internal method to load menu (IndexedDB first, then API)
   */
  private async loadMenuInternal(forceGet: boolean = false): Promise<any[]> {
    try {
      const store = await this._storeResolver.getSelectedStore();

      if (!store || !store.menuId) {
        throw new Error('Store or menuId not available');
      }

      // Try to load from IndexedDB first (unless forcing refresh)
      if (!forceGet) {
        const cachedMenu = await this.menuStorage.loadMenu(
          store.menuId,
          this.clientType
        );
        if (cachedMenu && Array.isArray(cachedMenu) && cachedMenu.length > 0) {
          console.log('Menu loaded from IndexedDB');
          return cachedMenu;
        }
      }

      // Load from API
      console.log('Loading menu from API...');
      return new Promise((resolve, reject) => {
        this._menuService
          .getMenu(store.menuId, this.clientType)
          .pipe(
            // Reject and unsubscribe the actual HttpClient request so a retry
            // starts a new request instead of joining a permanently pending
            // Promise kept in loadPromise.
            timeout(this.API_REQUEST_TIMEOUT_MS),
            map((res) => {
              if (!this.isCompleteMenuSnapshot(res)) {
                throw new Error(
                  'Invalid menu API response: expected a complete menu array'
                );
              }
              const menu = res;

              // Save to IndexedDB asynchronously (don't block)
              this.menuStorage
                .saveMenu(menu, store.menuId, this.clientType)
                .then(() => {
                  console.log('Menu saved to IndexedDB');
                })
                .catch((error) => {
                  console.error('Failed to save menu to IndexedDB:', error);
                  // Don't fail the request if IndexedDB save fails
                });

              return menu;
            }),
            catchError((error) => {
              console.error('API menu fetch failed:', error);
              return throwError(() => error);
            })
          )
          .subscribe({
            next: (menu) => resolve(menu),
            error: (error) => reject(error),
          });
      });
    } catch (error) {
      console.error('Menu load error:', error);
      throw error;
    }
  }

  private isCompleteMenuSnapshot(value: any): value is any[] {
    return (
      Array.isArray(value) &&
      value.every(
        (category) =>
          category?.id != null &&
          Array.isArray(category.products) &&
          category.products.every((product: any) => product?.id != null)
      )
    );
  }

  /**
   * Get sync object
   */
  getSyncObject(): Observable<any> {
    return this._menuService.getSyncObject();
  }

  /**
   * Set selected menu index
   */
  setSelectedMenuIndex(index: number): void {
    this.selectedMenuIndex.next(index);
  }

  /**
   * Clear menu data on logout
   */
  async clearMenu(): Promise<void> {
    this.menu = [];
    this.hasResolvedMenu = false;
    this.selectedMenuIndex.next(0);
    this.searchItem.next(null);

    // Clear IndexedDB
    try {
      await this.menuStorage.clearMenu();
    } catch (error) {
      console.error('Failed to clear menu from IndexedDB:', error);
    }
  }
}
